import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { DATA_PAGE } from "helpers/constants/pages";
import { setTemporaryExportData } from "redux/SingleDataset/actions";
import QueryModalWrapper from "./QueryModalWrapper";
import DataList, { Cluster } from "views/pages/DataPage/CardsView/DataList";
import MetadataSummary from "views/components/MetadataSummary";
import ImageDropBox from "views/pages/DataPage/CardsView/ImageDropBox";
import DataPageFilters from "views/pages/DataPage/CardsView/DataPageFilters";
import styles from "./style.module.scss";
import LabelPropagation from "views/components/LabelPropagation";
import { getIsLabelPropagationEnabled } from "redux/SingleDataset/selectors";
import { useLocation, useNavigate, useParams } from "react-router";
import { Option } from "views/uikit/FormFields/SelectList";
import {
  FLOW_PARAMETER,
  REVIEW_PARAMETER,
  SEED_PARAMETER,
} from "../../../../helpers/constants/miscellaneous";
import { useSeed } from "hooks/LabelPropagation/useSeed";
import ReviewList from "./ReviewList";
import ViewToggleButtons from "views/components/ViewToggleButtons";
import { useGetStats } from "hooks/LabelPropagation/useGetStats";
import classNames from "classnames";

interface CardsViewProps {
  clusters?: Cluster[];
  dynamicPage: string;
  navigationCluster: any;
  navigationClusterTags: any;
  isCardListLoading: boolean;
  navigationClusterLoader: boolean;
  metadataSummaryEnabled: boolean;
  isImageUploading: boolean;
  initialRightPanelMode?: "summary" | "labels" | null;
}

export type RightPanelMode = "summary" | "labels" | null;
type DataListPageType = "review" | "data";
const CardsView = ({
  clusters,
  dynamicPage,
  navigationCluster,
  navigationClusterTags,
  isCardListLoading,
  navigationClusterLoader,
  metadataSummaryEnabled,
  isImageUploading,
}: CardsViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { fetchStats } = useGetStats();

  const { datasetId, clusterId } = useParams();
  const initialRightPanelMode = searchParams.get(SEED_PARAMETER)
    ? "labels"
    : "summary";
  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>(
    initialRightPanelMode,
  );
  const [shouldFetch, setShouldFetch] = useState<string>();
  const [labelsOptions, setLabelOptions] = useState<Option[]>();
  const [displayNewFloatingPanel, setDisplayNewFloatingPanel] =
    useState<boolean>(false);
  const [selectedLabel, setSelectedLabel] = useState<string>();
  const [assignItemsToClass, setAssignItemsToClass] = useState<
    ((className: string) => void) | null
  >(null);

  const { seedId: fetchedSeedId, flowId: initialFlowId } = useSeed(datasetId);
  useEffect(() => {
    if (!datasetId || !initialFlowId) {
      return;
    }
    fetchStats(datasetId, initialFlowId);
  }, [datasetId, initialFlowId, fetchStats]);
  const [isMetadataSummaryOpen, setIsMetadataSummaryOpen] = useState(true);
  const dispatch = useDispatch();
  const isLabelPropagationEnabled = useSelector(getIsLabelPropagationEnabled);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("seed")) {
      setRightPanelMode("labels");
    } else if (metadataSummaryEnabled) {
      setRightPanelMode("summary");
    } else {
      setRightPanelMode(null);
    }
  }, [location.search, metadataSummaryEnabled, isLabelPropagationEnabled]);

  const labelPropagationProps = useMemo(
    () => ({
      onLabelClick: (value: string[]) => {
        setSelectedLabel(value.toString());
        if (assignItemsToClass && value.toString()) {
          assignItemsToClass(value.toString());
        }
      },
      options: labelsOptions,
      displayNewFloatingPanel:
        displayNewFloatingPanel && rightPanelMode === "labels",
      isLabelPropagationEnabled,
    }),
    [
      labelsOptions,
      displayNewFloatingPanel,
      rightPanelMode,
      assignItemsToClass,
      isLabelPropagationEnabled,
    ],
  );
  const isDataPage = dynamicPage === DATA_PAGE;
  const showMetadataSummary = isMetadataSummaryOpen && metadataSummaryEnabled;
  const dynamicLoader = isDataPage
    ? isCardListLoading
    : navigationClusterLoader;

  useEffect(() => {
    if (isImageUploading && isMetadataSummaryOpen) {
      setIsMetadataSummaryOpen(false);
    } else if (!isImageUploading && !isMetadataSummaryOpen) {
      setIsMetadataSummaryOpen(true);
    }

    return () => {
      dispatch(setTemporaryExportData(null));
    };
    //eslint-disable-next-line
  }, [isImageUploading]);

  const getRightPanel = () => {
    // Always show the unified right panel
    return (
      <>
        <MetadataSummary showMetadataSummary={rightPanelMode === "summary"} />
        {datasetId && rightPanelMode === "labels" && (
          <LabelPropagation
            datasetId={datasetId}
            assignItemsToClass={selectedLabel}
            onAssign={(assignFunc) => setAssignItemsToClass(() => assignFunc)}
            onDataChange={(value) => {
              setDisplayNewFloatingPanel(value.displayNewFloatingPanel);
              setLabelOptions(value.classesOptions);
            }}
            shouldFetch={shouldFetch}
          />
        )}
      </>
    );
  };

  const getDataPageFilters = () => {
    if (!isReview) return <DataPageFilters />;
  };

  const handleNavigate = (searchParams: URLSearchParams) => {
    navigate(
      { pathname: location.pathname, search: searchParams.toString() },
      { replace: true },
    );
  };

  const handleViewChange = (view: RightPanelMode) => {
    switch (view) {
      case "summary":
        const searchParams = new URLSearchParams(location.search);

        searchParams.delete(SEED_PARAMETER);
        searchParams.delete(FLOW_PARAMETER);
        searchParams.delete(REVIEW_PARAMETER);
        handleNavigate(searchParams);
        break;

      case "labels":
        if (rightPanelMode !== "labels") {
          const searchParams = new URLSearchParams(location.search);
          if (fetchedSeedId) {
            searchParams.set("seed", fetchedSeedId);
            if (initialFlowId) searchParams.set("flow", initialFlowId);
            handleNavigate(searchParams);
          } else {
            console.error(
              "Seed ID not found. Cannot navigate to label propagation.",
            );
          }
        } else {
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete(SEED_PARAMETER);
          searchParams.delete(FLOW_PARAMETER);
          searchParams.delete(REVIEW_PARAMETER);
          handleNavigate(searchParams);
        }
        break;
    }
  };
  const isReview = searchParams.has(REVIEW_PARAMETER);
  const dataType = isReview ? "review" : "data";
  const getDataByDataType: Record<DataListPageType, any> = {
    review: (
      <ReviewList
        onReviewAction={(sampleId?: string) => {
          setShouldFetch(sampleId);
        }}
      />
    ),
    data: (
      <DataList
        page={dynamicPage}
        clusters={clusters || []}
        navigationCluster={navigationCluster}
        navigationClusterTags={navigationClusterTags}
        isContentLoading={dynamicLoader}
        showMetadataSummary={showMetadataSummary}
        labelPropagation={labelPropagationProps}
        isDataPage={!!datasetId && !clusterId}
        rightPanelMode={rightPanelMode}
      />
    ),
  };

  //   TODO: change isCardListLoading && dataType === "data" to generic loader - VL-5946

  const leftPanelClassNames = classNames(styles.leftPanel, {
    [styles.leftPanelWithSummary]:
      rightPanelMode === "summary" && isLabelPropagationEnabled,
    [styles.leftPanelFullWidth]:
      rightPanelMode === "summary" && !isLabelPropagationEnabled,
    [styles.leftPanelWithLabels]:
      rightPanelMode === "labels" && isLabelPropagationEnabled,
  });

  const rightPanelClassNames = isLabelPropagationEnabled
    ? classNames(styles.rightPanel, {
        [styles.rightPanelWithSummary]: rightPanelMode === "summary",
        [styles.rightPanelWithLabels]: rightPanelMode === "labels",
      })
    : undefined;

  const innerBodyClassNames = classNames(styles.innerBody, {
    [styles.expandedInnerBody]: isReview,
  });

  return (
    <div
      className={styles.mainContainer}
      style={
        isCardListLoading && dataType === "data"
          ? { pointerEvents: "none" }
          : undefined
      }
    >
      <div className={leftPanelClassNames}>
        {getDataPageFilters()}
        <div className={innerBodyClassNames}>
          {isImageUploading ? <ImageDropBox /> : getDataByDataType[dataType]}
        </div>

        <QueryModalWrapper />
      </div>
      <div className={styles.rightPanelWrapper}>
        {!isLabelPropagationEnabled && (
          <div className={styles.metadataSummary}>
            <MetadataSummary
              showMetadataSummary={rightPanelMode === "summary"}
            />
          </div>
        )}
        {!!rightPanelMode && isLabelPropagationEnabled && (
          <div className={rightPanelClassNames}>
            <ViewToggleButtons
              currentView={rightPanelMode}
              onViewChange={handleViewChange}
            />
            {getRightPanel()}
          </div>
        )}
      </div>
    </div>
  );
};
export default CardsView;
